<template>
  <div class="container application">
    <div class="term-desc" />
    <validation-observer ref="validator" v-slot="{ errors }" tag="div">
      <h2 class="mt-2">추가 운전자 등록</h2>

      <div class="input-item">
        <div class="input-label">인원 수</div>
        <div :class="{ error: hasError(errors, '인원 수')}">
          <validator rules="required" name="인원 수">
            <select-box
              id="driverCount"
              :option="driverCounts"
              :default-option="false"
              :default-value="fellowDriverCount"
              @change="driverCountChange"
            />
          </validator>
        </div>
      </div>

      <div class="input-item">
        <div class="input-label">추가 운전자 쏘카ID</div>
        <vs-row v-for="(driver, index) in fellowDrivers" :key="index" class="fellow-driver">
          <vs-col vs-w="9">
            <input
              v-model="driver.username"
              name="driverUsername"
              class="form-field"
              type="text"
              @change="changeUser(index)"
            >
          </vs-col>
          <vs-col vs-w="3" class="button-wrapper">
            <button
              class="btn"
              :class="{ 'btn_outline': driver.checked }"
              @click.prevent="checkUser(driver, index)"
            >
              확인
            </button>
          </vs-col>
        </vs-row>
      </div>

      <div class="form-guide mt-2">
        <span class="blue050">- 운전면허 인증이 완료된 쏘카 회원에 한해 추가 운전자 등록이 가능합니다</span>
      </div>
    </validation-observer>

    <vs-row class="form-group submit mb-2">
      <vs-col vs-lg="6" vs-sm="6" vs-xs="12" class="bottom-button">
        <button
          type="submit"
          class="btn btn_lg btn_full btn_outline"
          @click.prevent="cancel"
        >
          이전
        </button>
      </vs-col>
      <vs-col vs-lg="6" vs-sm="6" vs-xs="12" class="bottom-button">
        <button
          type="submit"
          class="btn btn_lg btn_full"
          :disabled="!valid"
          @click.prevent="next"
        >
          다음
        </button>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>
import config from 'config';
import { get, isEmpty, find, assign, range, filter } from 'lodash';
import DialogUtil from 'utils/dialog';
import SelectBox from 'components/SelectBox';
import { isWebview } from 'utils/webview/isWebview';
import { closeWebView } from 'utils/webview/closeWebView';
export default {
  name: 'Step01',
  components: {
    SelectBox,
  },
  props: {
    limitFellowDriverCount: {
      type: Number,
      default: 4,
    },
    limitFellowDriver26DownCount: {
      type: Number,
      default: 4,
    },
    blockSecondLicense: {
      type: Boolean,
      default: false,
    },
    userChecker: {
      type: Function,
      required: true,
    },
    userEmail: {
      type: String,
      required: true,
    },
    data: {
      type: Array,
      default: () => ([]),
    },
  },
  data() {
    const driverCount = this.data.length === 0 ? get(this.driverCounts, '[0].id') || 1 : this.data.length;
    return {
      planHomeUrl: config.planHome.baseURL,
      fellowDriverCount: driverCount,
      fellowDrivers: this.getFellowDrivers(driverCount, this.data),
    };
  },
  computed: {
    valid() {
      const unchecked = find(this.fellowDrivers, (driver) => !driver.checked);
      return isEmpty(unchecked);
    },
    driverCounts() {
      return range(1, this.limitFellowDriverCount + 1).map((count) => ({ id: count, label: `${count}명` }));
    },
  },
  created() {
    this.$gtag.event('view', {
      page_name: 'plan_contract_extra_driver_1_add',
      event_category: document.title,
      page_url: document.URL,
    });
  },
  methods: {
    driverCountChange(val) {
      const driverCount = get(val, 'id', null);
      this.fellowDriverCount = driverCount;
      this.fellowDrivers = this.getFellowDrivers(driverCount, this.fellowDrivers);
    },
    next() {
      this.$emit('next', { fellowDrivers: this.fellowDrivers });
    },
    cancel() {
      DialogUtil.confirm('취소 하시겠습니까?', () => {
        if (isWebview()) {
          closeWebView();
          return;
        }
        window.location.href = this.planHomeUrl;
      });
    },
    getFellowDrivers(count, prev = []) {
      return range(count).map((idx) => {
        const exist = prev[idx];
        return exist || { index: idx, userId: '', age: '', name: '', checked: false };
      });
    },
    async checkUser({ username }, index) {
      if (username === this.userEmail) {
        DialogUtil.alert('자신은 추가 운전자로 등록할 수 없습니다.');
        return;
      }
      if (isEmpty(username)) return;
      const isAlreadyDriver = find(this.fellowDrivers, (driver) => (driver.username === username && driver.checked === true)) !== undefined;
      if (isAlreadyDriver) {
        DialogUtil.alert('동일한 추가 운전자가 이미 등록되어 있습니다.');
        return;
      }
      const result = await this.userChecker(username);
      if (result.userId != null) {
        const fellowDriver26DownCount = filter(this.fellowDrivers, (item) => (item.age < 26 && item.checked === true)).length;
        if (fellowDriver26DownCount >= this.limitFellowDriver26DownCount && result.age < 26) {
          DialogUtil.alert(`26세 미만 추가 운전자는 ${this.limitFellowDriver26DownCount}명으로 제한됩니다.`);
          return;
        }
        if (this.blockSecondLicense && result.liLv === 'License05') {
          DialogUtil.alert('해당 차종은 1종 면허만 추가 운전자 등록이 가능합니다.');
          return;
        }
        this.fellowDrivers[index] = assign(this.fellowDrivers[index], result, { checked: true });
        DialogUtil.alert(`${result.userId}님이 추가 운전자로 등록되었습니다.`);
      }
    },
    changeUser(index) {
      this.fellowDrivers[index].checked = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.fellow-driver {
  margin: 5px 0 0 0;
  .button-wrapper {
    padding-left: 5px;
  }
  & button {
    width: 100%;
    min-width: unset;
    height: 50px;
    padding: 0 0 0 0;
    @media (max-width: 768px) {
      height: 40px;
      padding: 0 0 0 0;
    }
  }
}
</style>
